/**
 * The aim menu — M2-03's called shots, made reachable (M2-06).
 *
 * The acceptance criterion is a *time*: under a second for a practised player,
 * keyboard only. That rules out a list you arrow through and confirm, and it
 * rules out a modal that has to be dismissed. What it leaves is the roguelike
 * answer — one key to open, one key to choose, and the blow is thrown — so the
 * whole interaction is two keystrokes and never more.
 *
 * Hotkeys are assigned by **zone**, not by part id, and that is what makes them
 * learnable across bodies: `h` is the head on a man, on a wolf and on a boar,
 * even though the part ids differ and a boar has four legs. Where a zone has a
 * left and a right, the first press takes whichever is less hurt — you aim at
 * the arm, and the game picks the one that is there.
 *
 * The menu is pure state. It knows nothing about the DOM and nothing about the
 * socket; `game.ts` opens it, feeds it keys, and sends what it returns.
 */

import type { BodyPartSnapshot } from '@annwn/protocol';

/**
 * Zone hotkeys, in the order the prompt lists them.
 *
 * Mnemonic where it can be (`h`ead, `n`eck, `c`hest, `a`rm…) and positional
 * where two zones collide: the eye is `e`, so the *b*elly takes `b`.
 */
export const AIM_KEYS: readonly { readonly key: string; readonly zone: string }[] = [
  { key: 'h', zone: 'head' },
  { key: 'e', zone: 'eye' },
  { key: 'n', zone: 'neck' },
  { key: 'c', zone: 'chest' },
  { key: 'b', zone: 'abdomen' },
  { key: 'a', zone: 'arm' },
  { key: 'd', zone: 'hand' },
  { key: 'g', zone: 'leg' },
  { key: 'f', zone: 'foot' },
];

const ZONE_OF_KEY = new Map(AIM_KEYS.map((entry) => [entry.key, entry.zone]));

/**
 * Which part a zone key resolves to.
 *
 * Least hurt first, so "aim at the arm" reaches the arm that is still working.
 * Ties break on part id, so the same body always answers the same way and a
 * replayed session presses the same key to the same effect.
 */
export function resolveZone(
  parts: readonly BodyPartSnapshot[],
  zone: string,
): BodyPartSnapshot | null {
  const candidates = parts
    .filter((part) => part.zone === zone)
    .sort((left, right) => left.level - right.level || left.id.localeCompare(right.id));
  return candidates[0] ?? null;
}

/** What the prompt offers, given the body actually in front of the player. */
export function aimChoices(
  parts: readonly BodyPartSnapshot[],
): readonly { readonly key: string; readonly part: BodyPartSnapshot }[] {
  const choices: { key: string; part: BodyPartSnapshot }[] = [];
  for (const { key, zone } of AIM_KEYS) {
    const part = resolveZone(parts, zone);
    if (part) choices.push({ key, part });
  }
  return choices;
}

/** A one-line prompt: `head (h) · neck (n) · chest (c)…`. */
export function aimPrompt(parts: readonly BodyPartSnapshot[]): string {
  const offered = aimChoices(parts)
    .map(({ key, part }) => `${zoneLabel(part.zone)} (${key})`)
    .join(' · ');
  return offered.length > 0 ? `Aim at: ${offered}` : 'Nothing to aim at.';
}

/** The zone's own word, so the prompt says "arm" rather than "near foreleg". */
function zoneLabel(zone: string): string {
  return zone === 'abdomen' ? 'belly' : zone;
}

/**
 * The menu, as a small state machine.
 *
 * Deliberately not a mode with its own input handling: it is open or it is not,
 * and one key either resolves it or closes it. Anything longer-lived would
 * fight the chat line and the look cursor for the keyboard, which is the bug
 * `setChatOpen` already exists to avoid.
 */
export class AimMenu {
  private parts: readonly BodyPartSnapshot[] = [];
  private open = false;

  get active(): boolean {
    return this.open;
  }

  /** Open against a body. Refused when there is no body to aim at. */
  show(parts: readonly BodyPartSnapshot[]): string | null {
    if (parts.length === 0) return null;
    this.parts = parts;
    this.open = true;
    return aimPrompt(parts);
  }

  close(): void {
    this.open = false;
    this.parts = [];
  }

  /**
   * Feed it a key. Returns the part to aim at, or `null` — and closes either
   * way, because a menu that stayed open on a miss would be a menu a player has
   * to escape out of, which is the second keystroke the criterion cannot afford.
   */
  choose(key: string): BodyPartSnapshot | null {
    if (!this.open) return null;

    const zone = ZONE_OF_KEY.get(key.toLowerCase());
    const part = zone ? resolveZone(this.parts, zone) : null;
    this.close();
    return part;
  }
}
